import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Edit, Trash2, Mail, X, Star } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { name: '', type: 'quote', subject: '', body_html: '', is_default: false };

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showPreview, setShowPreview] = useState(false);
  const [signature, setSignature] = useState('');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [tplRes, sigRes] = await Promise.all([
        axiosInstance.get('/email/templates'),
        axiosInstance.get('/email/signature'),
      ]);
      setTemplates(tplRes.data || []);
      setSignature(sigRes.data.signature_html || '');
    } catch (err) {
      toast.error('Fout bij laden templates');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setShowPreview(false);
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, type: t.type, subject: t.subject, body_html: t.body_html, is_default: !!t.is_default });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Geef de template een naam'); return; }
    if (!form.subject.trim()) { toast.error('Vul een onderwerp in'); return; }
    if (!form.body_html.trim()) { toast.error('Vul de inhoud in'); return; }
    try {
      if (editingId) {
        await axiosInstance.put(`/email/templates/${editingId}`, form);
        toast.success('Template bijgewerkt');
      } else {
        await axiosInstance.post('/email/templates', form);
        toast.success('Template aangemaakt');
      }
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fout bij opslaan');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deze template verwijderen?')) return;
    try {
      await axiosInstance.delete(`/email/templates/${id}`);
      toast.success('Template verwijderd');
      fetchAll();
    } catch (err) {
      toast.error('Fout bij verwijderen');
    }
  };

  const previewHtml = () => {
    return (form.body_html || '')
      .replaceAll('{number}', '2026-00064')
      .replaceAll('{client_name}', 'Stella van der Horst')
      .replaceAll('{total}', '€ 1.250,00')
      .replaceAll('{public_link}', 'https://dashboard.cardosobouw.nl/offerte/voorbeeld-token')
      .replaceAll('{payment_link}', 'https://dashboard.cardosobouw.nl/factuur/voorbeeld-token')
      .replaceAll('{signature}', signature || '');
  };

  const handleSendSummaryNow = async () => {
    try {
      const r = await axiosInstance.post('/email/send-daily-summary');
      if (r.data.sent) {
        toast.success('Dagoverzicht verzonden naar info@cardosobouw.nl');
      } else {
        toast.info(r.data.reason || 'Geen e-mails verzonden vandaag');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fout bij verzenden');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h1 className="text-4xl font-bold text-gray-800">E-mail templates</h1>
        <div className="flex gap-2">
          <Button
            onClick={handleSendSummaryNow}
            variant="outline"
            className="border-[#3da5a5] text-[#3da5a5] hover:bg-[#3da5a5]/5"
            data-testid="send-summary-now-btn"
          >
            <Mail className="w-4 h-4 mr-2" /> Dagoverzicht nu sturen
          </Button>
          <Button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-[#3da5a5] hover:bg-[#2d8585]"
            data-testid="new-template-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Nieuwe template
          </Button>
        </div>
      </div>

      <div className="bg-[#3da5a5]/5 border border-[#3da5a5]/30 rounded-lg p-3 text-sm text-gray-700">
        <strong>📨 Automatisch dagoverzicht:</strong> Elke dag om 22:00 (Amsterdam tijd) ontvangt u op <strong>info@cardosobouw.nl</strong> een overzicht van alle e-mails die vandaag verzonden zijn vanuit het dashboard.
      </div>

      <p className="text-sm text-gray-500">
        Beschikbare placeholders: <code className="bg-gray-100 px-1 rounded">{`{number}`}</code>{' '}
        <code className="bg-gray-100 px-1 rounded">{`{client_name}`}</code>{' '}
        <code className="bg-gray-100 px-1 rounded">{`{total}`}</code>{' '}
        <code className="bg-gray-100 px-1 rounded">{`{public_link}`}</code>{' '}
        <code className="bg-gray-100 px-1 rounded">{`{payment_link}`}</code>{' '}
        <code className="bg-gray-100 px-1 rounded">{`{sent_date}`}</code>{' '}
        <code className="bg-gray-100 px-1 rounded">{`{expiry_date}`}</code>{' '}
        <code className="bg-gray-100 px-1 rounded">{`{signature}`}</code>
      </p>

      {showForm && (
        <Card className="bg-white shadow-sm border-gray-200" data-testid="template-form">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-gray-800">{editingId ? 'Template bewerken' : 'Nieuwe template'}</CardTitle>
            <Button variant="ghost" size="sm" onClick={resetForm}><X className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label className="text-gray-700">Naam *</Label>
                <Input
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="bijv. Standaard offerte"
                  data-testid="template-name-input"
                />
              </div>
              <div>
                <Label className="text-gray-700">Type *</Label>
                <Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="template-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quote">Offerte</SelectItem>
                    <SelectItem value="invoice">Factuur</SelectItem>
                    <SelectItem value="quote_reminder_7d">Herinnering offerte 7 dagen</SelectItem>
                    <SelectItem value="quote_reminder_expiry">Herinnering offerte vervaldatum</SelectItem>
                    <SelectItem value="invoice_reminder_due">Attendering factuur vervaldatum</SelectItem>
                    <SelectItem value="invoice_herinnering_1">Factuur — stap 1 Betalingsherinnering</SelectItem>
                    <SelectItem value="invoice_aanmaning">Factuur — stap 2 Aanmaning</SelectItem>
                    <SelectItem value="invoice_wik">Factuur — stap 3 14-dagenbrief (ingebrekestelling)</SelectItem>
                    <SelectItem value="invoice_incasso_aankondiging">Factuur — laatste herinnering vóór incasso (handmatig)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-gray-700">Onderwerp *</Label>
              <Input
                value={form.subject}
                onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                placeholder="bijv. Offerte {number} — Cardoso Bouw"
                data-testid="template-subject-input"
              />
            </div>

            <div>
              <Label className="text-gray-700">Inhoud (HTML toegestaan) *</Label>
              <textarea
                value={form.body_html}
                onChange={e => setForm(f => ({ ...f, body_html: e.target.value }))}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-800 h-64 font-mono"
                data-testid="template-body-textarea"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={form.is_default}
                onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))}
                data-testid="template-default-checkbox"
              />
              <label htmlFor="is_default" className="text-sm text-gray-700">
                Standaard template voor {
                  form.type === 'quote' ? 'offertes' :
                  form.type === 'invoice' ? 'facturen' :
                  form.type === 'quote_reminder_7d' ? 'herinnering offerte 7 dagen' :
                  form.type === 'quote_reminder_expiry' ? 'herinnering offerte vervaldatum' :
                  form.type === 'invoice_reminder_due' ? 'attendering factuur vervaldatum' :
                  form.type === 'invoice_herinnering_1' ? 'betalingsherinnering (stap 1)' :
                  form.type === 'invoice_aanmaning' ? 'aanmaning (stap 2)' :
                  form.type === 'invoice_wik' ? '14-dagenbrief / ingebrekestelling (stap 3)' :
                  form.type === 'invoice_incasso_aankondiging' ? 'laatste herinnering vóór incasso' :
                  form.type
                }
              </label>
            </div>

            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPreview(s => !s)}
                data-testid="template-toggle-preview"
              >
                {showPreview ? 'Voorvertoning verbergen' : 'Voorvertoning tonen'}
              </Button>
            </div>

            {showPreview && (
              <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid="template-preview">
                <p className="text-xs text-gray-500 mb-2">Voorvertoning met voorbeeldwaarden:</p>
                <div dangerouslySetInnerHTML={{ __html: previewHtml() }} />
              </div>
            )}

            <div className="flex justify-end pt-2 border-t border-gray-200">
              <Button
                onClick={handleSubmit}
                className="bg-[#3da5a5] hover:bg-[#2d8585]"
                data-testid="save-template-btn"
              >
                {editingId ? 'Bijwerken' : 'Opslaan'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <p className="text-gray-500 text-center py-8">Laden...</p>
      ) : templates.length === 0 ? (
        <Card className="bg-white shadow-sm border-gray-200">
          <CardContent className="py-12 text-center">
            <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Geen templates beschikbaar</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {templates.map(t => {
            const typeMeta = {
              quote: { icon: 'text-blue-500', badge: 'bg-blue-100 text-blue-700', label: 'Offerte' },
              invoice: { icon: 'text-purple-500', badge: 'bg-purple-100 text-purple-700', label: 'Factuur' },
              quote_reminder_7d: { icon: 'text-amber-500', badge: 'bg-amber-100 text-amber-700', label: 'Herinnering offerte 7d' },
              quote_reminder_expiry: { icon: 'text-red-500', badge: 'bg-red-100 text-red-700', label: 'Herinnering offerte vervaldatum' },
              invoice_reminder_due: { icon: 'text-orange-500', badge: 'bg-orange-100 text-orange-700', label: 'Attendering factuur vervaldatum' },
              invoice_herinnering_1: { icon: 'text-amber-500', badge: 'bg-amber-100 text-amber-700', label: 'Stap 1 — Betalingsherinnering' },
              invoice_aanmaning: { icon: 'text-orange-600', badge: 'bg-orange-100 text-orange-800', label: 'Stap 2 — Aanmaning' },
              invoice_wik: { icon: 'text-red-600', badge: 'bg-red-100 text-red-700', label: 'Stap 3 — 14-dagenbrief' },
              invoice_incasso_aankondiging: { icon: 'text-red-700', badge: 'bg-red-200 text-red-800', label: 'Laatste herinnering vóór incasso' },
            }[t.type] || { icon: 'text-gray-500', badge: 'bg-gray-100 text-gray-700', label: t.type };
            return (
            <Card key={t.id} className="bg-white shadow-sm border-gray-200" data-testid={`template-${t.id}`}>
              <CardContent className="py-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <Mail className={`w-5 h-5 ${typeMeta.icon}`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-800">{t.name}</p>
                      {t.is_default && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded flex items-center gap-1">
                          <Star className="w-3 h-3" /> Standaard
                        </span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded ${typeMeta.badge}`}>
                        {typeMeta.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{t.subject}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(t)}
                    className="text-gray-600"
                    data-testid={`edit-template-${t.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(t.id)}
                    className="text-red-500"
                    data-testid={`delete-template-${t.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );})}
        </div>
      )}
    </div>
  );
};

export default EmailTemplates;
